import UserGroupIcon from '@icons/user-group'
import DocumentTextIcon from '@icons/document-text'
import PhoneIcon from '@icons/phone'
import AtSymbolIcon from '@icons/at-symbol'
import { LEGAL_LINKS } from '@/lib/definitions/constants'

type HelpTheme = 'dark' | 'light'

const HelpPanelCompact = ({ theme = 'dark' }: { theme?: HelpTheme }) => {
  const isDark = theme === 'dark'
  const shell = isDark
    ? 'overflow-hidden rounded-lg bg-surface/[0.06] ring-1 ring-inset ring-white/10'
    : 'overflow-hidden rounded-lg border border-hairline/80 bg-surface'
  const linkClass = isDark
    ? 'group flex min-w-0 items-center gap-2.5 px-2.5 py-1.5 text-sm font-medium text-white/95 transition hover:bg-surface/10'
    : 'group flex min-w-0 items-center gap-2.5 px-2.5 py-1.5 text-sm text-neutral-60 transition hover:bg-neutral-05/90'
  const iconWrap = isDark ? 'text-white/50' : 'text-neutral-40'
  const band = isDark
    ? 'bg-surface/[0.04] px-2 py-1'
    : 'bg-neutral-05/70 px-2.5 py-1'

  return (
    <div className={isDark ? 'text-white' : 'text-neutral-70'}>
      <div className={shell}>
        <div className={band}>
          <p
            className={
              isDark
                ? 'text-sm font-medium text-white/60'
                : 'text-sm font-medium text-neutral-50'
            }
          >
            Business Hours are Monday to Friday 9 a.m. to 5 p.m. CET/CEST.
          </p>
        </div>
        <div className="flex flex-col py-0.5">
          <a href="tel:+4930577037417" className={linkClass}>
            <span className={`shrink-0 tabular-nums ${iconWrap}`}>
              <PhoneIcon className="size-[15px]" />
            </span>
            <span className="min-w-0 truncate tabular-nums">
              +49 30 5770 374 17
            </span>
          </a>
          <a href="mailto:support@inter.link" className={linkClass}>
            <span className={`shrink-0 ${iconWrap}`}>
              <AtSymbolIcon className="size-[15px]" />
            </span>
            <span className="min-w-0 truncate">support@inter.link</span>
          </a>
        </div>
        <div
          className={`border-t ${isDark ? 'border-white/10' : 'border-neutral-10'} ${band}`}
        >
          <p
            className={
              isDark
                ? 'text-sm font-medium text-white/60'
                : 'text-sm font-medium text-neutral-50'
            }
          >
            For our 24/7 emergency hotline please contact:
          </p>
        </div>
        <div className="flex flex-col py-0.5">
          <a href="tel:+4930577037417" className={linkClass}>
            <span className={`shrink-0 ${iconWrap}`}>
              <PhoneIcon className="size-[15px]" />
            </span>
            <span className="min-w-0 truncate tabular-nums">
              +49 30 5770 374 14
            </span>
          </a>
          <a
            href={LEGAL_LINKS.ESCALATION_MATRIX}
            target="_blank"
            rel="noopener noreferrer"
            className={linkClass}
          >
            <span className={`shrink-0 ${iconWrap}`}>
              <DocumentTextIcon className="size-[15px]" />
            </span>
            <span className="min-w-0 truncate">Escalation matrix (PDF)</span>
          </a>
        </div>
      </div>
    </div>
  )
}

export const HelpBody = ({
  theme = 'dark',
  compact = false,
  panel = false,
}: {
  theme?: HelpTheme
  compact?: boolean
  panel?: boolean
}) => {
  const isDark = theme === 'dark'

  if (panel) {
    return <HelpPanelCompact theme={theme} />
  }

  const titleClass = compact
    ? isDark
      ? 'text-base font-semibold text-white'
      : 'text-base font-semibold text-neutral-90'
    : isDark
      ? 'text-heading-2 text-white'
      : 'text-heading-2 text-neutral-90'

  const bodyClass = compact
    ? isDark
      ? 'text-sm leading-snug text-white/90'
      : 'text-sm leading-snug text-neutral-60'
    : isDark
      ? 'leading-5 text-white/90'
      : 'leading-5 text-neutral-70'

  const sectionMetaClass = compact
    ? isDark
      ? 'mb-2 text-sm text-white/70'
      : 'mb-2 text-sm text-neutral-60'
    : isDark
      ? 'mb-2 ml-0 text-white/70'
      : 'mb-2 ml-0 text-neutral-60'

  const blockSpacing = compact ? 'mt-5' : 'mt-8'

  return (
    <div className={isDark ? 'text-white' : 'text-neutral-70'}>
      <div
        className={
          compact
            ? 'flex flex-row items-start gap-3 relative z-10'
            : 'flex flex-row relative z-10 space-x-4'
        }
      >
        {!compact && (
          <div className="w-2/5">
            <div className="w-full flex items-center justify-center">
              <UserGroupIcon
                className={
                  isDark
                    ? 'size-24 text-white/20'
                    : 'size-24 text-neutral-90/15'
                }
              />
            </div>
          </div>
        )}

        {compact && (
          <div className="shrink-0 rounded-lg bg-neutral-100 p-2 dark:bg-surface/10">
            <UserGroupIcon
              className={
                isDark ? 'size-5 text-white/80' : 'size-5 text-neutral-60'
              }
            />
          </div>
        )}

        <div className={compact ? 'min-w-0 flex-1 space-y-2' : 'space-y-4'}>
          <h3 className={titleClass}>Support</h3>

          <div className={bodyClass}>
            Found a bug or have a suggestion for how we can improve the portal?
            Let us know and we’ll get to work on it.
          </div>
        </div>
      </div>

      <div className={`${blockSpacing} relative`}>
        <div className={sectionMetaClass}>
          Business Hours are Monday to Friday 9 a.m. to 5 p.m. CET/CEST.
        </div>
        <div
          className={
            compact
              ? isDark
                ? 'flex flex-col gap-3 p-3 bg-surface/10 relative z-10 rounded-lg'
                : 'flex flex-col gap-3 p-3 bg-neutral-05 relative z-10 rounded-lg border border-neutral-10'
              : isDark
                ? 'flex justify-between p-3 bg-surface/10 relative z-10 rounded-lg'
                : 'flex justify-between p-3 bg-neutral-05 relative z-10 rounded-lg border border-neutral-10'
          }
        >
          <div
            className={
              isDark
                ? 'flex items-center gap-2 hover:text-white hover:scale-105 transition'
                : 'flex items-center gap-2 hover:text-neutral-90 hover:scale-105 transition'
            }
          >
            <span
              className={isDark ? 'w-5 text-white/80' : 'w-5 text-neutral-50'}
            >
              <PhoneIcon className="size-4" />
            </span>
            <a href="tel:+4930577037417">+49 30 5770 374 17</a>
          </div>

          <div
            className={
              isDark
                ? 'flex items-center gap-2 hover:text-white hover:scale-105 transition'
                : 'flex items-center gap-2 hover:text-neutral-90 hover:scale-105 transition'
            }
          >
            <span
              className={isDark ? 'w-5 text-white/80' : 'w-5 text-neutral-50'}
            >
              <AtSymbolIcon className="size-4" />
            </span>
            <a href="mailto:support@inter.link">support@inter.link</a>
          </div>
        </div>
      </div>

      <div className={`${blockSpacing} relative`}>
        <div className={sectionMetaClass}>
          For our <strong>24/7 emergency hotline</strong> please contact:
        </div>

        <div
          className={
            compact
              ? isDark
                ? 'flex flex-col gap-3 p-3 bg-surface/10 relative z-10 rounded-lg'
                : 'flex flex-col gap-3 p-3 bg-neutral-05 relative z-10 rounded-lg border border-neutral-10'
              : isDark
                ? 'flex justify-between p-3 bg-surface/10 relative z-10 rounded-lg'
                : 'flex justify-between p-3 bg-neutral-05 relative z-10 rounded-lg border border-neutral-10'
          }
        >
          <div
            className={
              isDark
                ? 'flex items-center gap-2 hover:text-white hover:scale-105 transition'
                : 'flex items-center gap-2 hover:text-neutral-90 hover:scale-105 transition'
            }
          >
            <span
              className={isDark ? 'w-5 text-white/80' : 'w-5 text-neutral-50'}
            >
              <PhoneIcon className="size-4" />
            </span>
            <a href="tel:+4930577037417">+49 30 5770 374 14</a>
          </div>

          <div
            className={
              isDark
                ? 'flex items-center gap-2 hover:text-white hover:scale-105 transition'
                : 'flex items-center gap-2 hover:text-neutral-90 hover:scale-105 transition'
            }
          >
            <span
              className={isDark ? 'w-5 text-white/80' : 'w-5 text-neutral-50'}
            >
              <DocumentTextIcon className="size-4" />
            </span>
            <a
              href={LEGAL_LINKS.ESCALATION_MATRIX}
              target="_blank"
              rel="noopener noreferrer"
            >
              Escalation Matrix
            </a>
          </div>
        </div>
      </div>
    </div>
  )
}

export const Help = ({
  embedded = false,
  compact = false,
  panel = false,
  theme = 'dark',
}: {
  embedded?: boolean
  compact?: boolean
  panel?: boolean
  theme?: HelpTheme
}) => {
  if (embedded)
    return <HelpBody theme={theme} compact={compact} panel={panel} />

  if (theme === 'light') {
    return (
      <div className="w-full p-6 relative z-30 rounded-xl bg-neutral-05 border border-neutral-10 shadow-card text-neutral-70">
        <HelpBody theme="light" />
      </div>
    )
  }

  return (
    <div className="w-full sm:w-[600px] p-6 relative z-30 rounded-xl shadow-popover text-accent-10 bg-brand-1 bg-linear-to-tl from-neutral-90 to-80% to-auxiliary-2/40">
      <HelpBody theme="dark" />
    </div>
  )
}
